/**
 * Cohorts API Service
 *
 * Full lifecycle for instructor-owned cohorts: list, detail, create/edit,
 * archive/delete, enrollment (single + bulk CSV), and progress analytics.
 *
 * NOTE: this module deliberately re-exports `Cohort` as `InstructorCohort`
 * (and keeps the local name `Cohort` for ergonomics) because the existing
 * `Cohort` type in `../types` is announcement-scoped with a different shape
 * (`enrolledCount`/`maxCapacity`/`atRiskCount` vs the richer shape this
 * page needs). Don't replace the legacy type — `announcementsApi.ts` and
 * `Announcements.tsx` still depend on it.
 */

// Cohort endpoints live in session-manager (:3001) under /cohorts/*, gated to
// role=instructor|admin. See services/session-manager/src/routes/cohorts.ts.
import { sessionManagerApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import type { PaginatedResponse } from '../types';

// =====================================================
// TYPES
// =====================================================

export type CohortStatus = 'planned' | 'active' | 'completed' | 'archived';

export interface Cohort {
  id: string;
  name: string;
  description: string;
  courseId: string;
  courseName: string;
  instructorId: string;
  instructorName: string;
  startDate: string; // ISO date (yyyy-mm-dd)
  endDate: string;
  studentCount: number;
  capacity: number;
  status: CohortStatus;
  enrolledStudents: string[];
  completionRate: number; // 0..100
  avgProgress: number; // 0..100
  createdAt: string;
  updatedAt: string;
}

export interface CohortInput {
  name: string;
  description: string;
  courseId: string;
  capacity: number;
  startDate: string;
  endDate: string;
}

export interface CohortFilters {
  search?: string;
  status?: CohortStatus | 'all';
  courseId?: string;
  page?: number;
  limit?: number;
}

export interface CohortProgress {
  students: Array<{
    studentId: string;
    name: string;
    completedLabs: number;
    totalLabs: number;
    lastActive: string;
  }>;
  byLab: Array<{
    labId: string;
    labTitle: string;
    completionRate: number;
  }>;
}

export interface CohortStudentRow {
  studentId: string;
  name: string;
  email: string;
  enrolledAt: string;
  completedLabs: number;
  totalLabs: number;
  lastActive: string;
  status: 'on-track' | 'behind' | 'at-risk' | 'ahead';
}

export interface BulkEnrollResult {
  enrolled: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
}

// =====================================================
// DETERMINISTIC MOCK DATA
// =====================================================
// Deterministic timestamps relative to a fixed reference so dev mode is
// stable across reloads (no "5 minutes ago" jitter on every refresh).

const MOCK_NOW = new Date('2026-05-23T12:00:00Z').getTime();
const days = (n: number) => new Date(MOCK_NOW - n * 86_400_000).toISOString();
const daysFwd = (n: number) =>
  new Date(MOCK_NOW + n * 86_400_000).toISOString().slice(0, 10);
const daysBack = (n: number) =>
  new Date(MOCK_NOW - n * 86_400_000).toISOString().slice(0, 10);

const MOCK_COHORTS: Cohort[] = [
  {
    id: 'cohort-2026-a',
    name: 'Cohort 2026-A LLM Fundamentals',
    description:
      'First spring cohort focusing on prompt engineering, embeddings, and OpenAI / Anthropic SDK basics.',
    courseId: 'course-llm-fundamentals',
    courseName: 'LLM Fundamentals',
    instructorId: 'instructor-1',
    instructorName: 'Dr. Ayesha Khan',
    startDate: daysBack(45),
    endDate: daysFwd(45),
    studentCount: 28,
    capacity: 35,
    status: 'active',
    enrolledStudents: Array.from({ length: 28 }, (_, i) => `student-${i + 1}`),
    completionRate: 62,
    avgProgress: 71,
    createdAt: days(60),
    updatedAt: days(2),
  },
  {
    id: 'cohort-2026-b',
    name: 'Cohort 2026-B RAG Engineering',
    description:
      'Hands-on retrieval-augmented generation: vector stores, chunking, re-ranking, and evaluation harnesses.',
    courseId: 'course-rag-engineering',
    courseName: 'RAG Engineering',
    instructorId: 'instructor-1',
    instructorName: 'Dr. Ayesha Khan',
    startDate: daysBack(14),
    endDate: daysFwd(75),
    studentCount: 22,
    capacity: 30,
    status: 'active',
    enrolledStudents: Array.from({ length: 22 }, (_, i) => `student-${100 + i}`),
    completionRate: 18,
    avgProgress: 24,
    createdAt: days(30),
    updatedAt: days(1),
  },
  {
    id: 'cohort-2026-c',
    name: 'Cohort 2026-C Production AI',
    description:
      'Capstone cohort: shipping AI products end-to-end — evals, observability, cost controls, and deployment.',
    courseId: 'course-production-ai',
    courseName: 'Production AI Systems',
    instructorId: 'instructor-1',
    instructorName: 'Dr. Ayesha Khan',
    startDate: daysFwd(14),
    endDate: daysFwd(104),
    studentCount: 0,
    capacity: 25,
    status: 'planned',
    enrolledStudents: [],
    completionRate: 0,
    avgProgress: 0,
    createdAt: days(7),
    updatedAt: days(1),
  },
  {
    id: 'cohort-2025-d',
    name: 'Cohort 2025-D Multimodal AI',
    description:
      'Vision + audio models — completed in spring 2025. Archived for historical reference.',
    courseId: 'course-multimodal',
    courseName: 'Multimodal AI',
    instructorId: 'instructor-1',
    instructorName: 'Dr. Ayesha Khan',
    startDate: daysBack(180),
    endDate: daysBack(60),
    studentCount: 24,
    capacity: 30,
    status: 'completed',
    enrolledStudents: Array.from({ length: 24 }, (_, i) => `student-${200 + i}`),
    completionRate: 96,
    avgProgress: 98,
    createdAt: days(220),
    updatedAt: days(60),
  },
];

const MOCK_COURSES: Array<{ id: string; name: string }> = [
  { id: 'course-llm-fundamentals', name: 'LLM Fundamentals' },
  { id: 'course-rag-engineering', name: 'RAG Engineering' },
  { id: 'course-production-ai', name: 'Production AI Systems' },
  { id: 'course-multimodal', name: 'Multimodal AI' },
  { id: 'course-fine-tuning', name: 'Fine-tuning & LoRA' },
  { id: 'course-agents', name: 'AI Agents' },
];

/**
 * Dev-mode in-memory mutations so the UI feels real even without a backend.
 * Kept inside this module so it can't leak. Reset on page reload.
 */
const mockState: { cohorts: Cohort[] } = {
  cohorts: MOCK_COHORTS.map((c) => ({ ...c, enrolledStudents: [...c.enrolledStudents] })),
};

function applyFilters(list: Cohort[], filters: CohortFilters): Cohort[] {
  let out = list;
  if (filters.search) {
    const q = filters.search.toLowerCase();
    out = out.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.courseName.toLowerCase().includes(q)
    );
  }
  if (filters.status && filters.status !== 'all') {
    out = out.filter((c) => c.status === filters.status);
  }
  if (filters.courseId) {
    out = out.filter((c) => c.courseId === filters.courseId);
  }
  return out;
}

// =====================================================
// API: COHORTS
// =====================================================

export async function getCohorts(filters: CohortFilters = {}): Promise<PaginatedResponse<Cohort>> {
  try {
    const response = await sessionManagerApi.get('/cohorts', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch cohorts:', error);
    const filtered = applyFilters(mockState.cohorts, filters);
    const page = filters.page || 1;
    const limit = filters.limit || 20;
    const mock: PaginatedResponse<Cohort> = {
      data: filtered,
      pagination: {
        page,
        limit,
        total: filtered.length,
        totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
      },
    };
    return mockFallback(error, mock);
  }
}

export async function getCohort(id: string): Promise<Cohort> {
  try {
    const response = await sessionManagerApi.get(`/cohorts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch cohort:', error);
    const found = mockState.cohorts.find((c) => c.id === id);
    if (found) return mockFallback(error, found);
    throw error;
  }
}

export async function createCohort(input: CohortInput): Promise<Cohort> {
  try {
    const response = await sessionManagerApi.post('/cohorts', input);
    return response.data;
  } catch (error) {
    console.error('Failed to create cohort:', error);
    const course = MOCK_COURSES.find((c) => c.id === input.courseId);
    const now = new Date().toISOString();
    const created: Cohort = {
      id: `cohort-${Date.now()}`,
      name: input.name,
      description: input.description,
      courseId: input.courseId,
      courseName: course?.name || 'Unknown Course',
      instructorId: 'instructor-1',
      instructorName: 'Dr. Ayesha Khan',
      startDate: input.startDate,
      endDate: input.endDate,
      studentCount: 0,
      capacity: input.capacity,
      status: 'planned',
      enrolledStudents: [],
      completionRate: 0,
      avgProgress: 0,
      createdAt: now,
      updatedAt: now,
    };
    mockState.cohorts = [created, ...mockState.cohorts];
    return mockFallback(error, created);
  }
}

export async function updateCohort(id: string, input: Partial<CohortInput>): Promise<Cohort> {
  try {
    const response = await sessionManagerApi.patch(`/cohorts/${id}`, input);
    return response.data;
  } catch (error) {
    console.error('Failed to update cohort:', error);
    const idx = mockState.cohorts.findIndex((c) => c.id === id);
    if (idx === -1) throw error;
    const course = input.courseId
      ? MOCK_COURSES.find((c) => c.id === input.courseId)
      : undefined;
    const updated: Cohort = {
      ...mockState.cohorts[idx],
      ...input,
      ...(course ? { courseName: course.name } : {}),
      updatedAt: new Date().toISOString(),
    };
    mockState.cohorts[idx] = updated;
    return mockFallback(error, updated);
  }
}

export async function archiveCohort(id: string): Promise<Cohort> {
  try {
    const response = await sessionManagerApi.post(`/cohorts/${id}/archive`);
    return response.data;
  } catch (error) {
    console.error('Failed to archive cohort:', error);
    const idx = mockState.cohorts.findIndex((c) => c.id === id);
    if (idx === -1) throw error;
    mockState.cohorts[idx] = {
      ...mockState.cohorts[idx],
      status: 'archived',
      updatedAt: new Date().toISOString(),
    };
    return mockFallback(error, mockState.cohorts[idx]);
  }
}

export async function deleteCohort(id: string): Promise<void> {
  try {
    await sessionManagerApi.delete(`/cohorts/${id}`);
  } catch (error) {
    console.error('Failed to delete cohort:', error);
    mockState.cohorts = mockState.cohorts.filter((c) => c.id !== id);
    return mockFallback(error, undefined);
  }
}

// =====================================================
// API: ENROLLMENT
// =====================================================

export async function enrollStudents(cohortId: string, studentIds: string[]): Promise<Cohort> {
  try {
    const response = await sessionManagerApi.post(`/cohorts/${cohortId}/enrollments`, {
      studentIds,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to enroll students:', error);
    const idx = mockState.cohorts.findIndex((c) => c.id === cohortId);
    if (idx === -1) throw error;
    const existing = new Set(mockState.cohorts[idx].enrolledStudents);
    studentIds.forEach((id) => existing.add(id));
    const enrolledStudents = Array.from(existing);
    mockState.cohorts[idx] = {
      ...mockState.cohorts[idx],
      enrolledStudents,
      studentCount: enrolledStudents.length,
      updatedAt: new Date().toISOString(),
    };
    return mockFallback(error, mockState.cohorts[idx]);
  }
}

export async function removeStudent(cohortId: string, studentId: string): Promise<Cohort> {
  try {
    const response = await sessionManagerApi.delete(
      `/cohorts/${cohortId}/enrollments/${studentId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to remove student from cohort:', error);
    const idx = mockState.cohorts.findIndex((c) => c.id === cohortId);
    if (idx === -1) throw error;
    const enrolledStudents = mockState.cohorts[idx].enrolledStudents.filter(
      (s) => s !== studentId
    );
    mockState.cohorts[idx] = {
      ...mockState.cohorts[idx],
      enrolledStudents,
      studentCount: enrolledStudents.length,
      updatedAt: new Date().toISOString(),
    };
    return mockFallback(error, mockState.cohorts[idx]);
  }
}

export async function bulkEnrollFromCsv(
  cohortId: string,
  file: File
): Promise<BulkEnrollResult> {
  const form = new FormData();
  form.append('file', file);
  try {
    const response = await sessionManagerApi.post(
      `/cohorts/${cohortId}/enrollments/bulk`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to bulk enroll:', error);
    // Mock: read CSV in browser, treat first column as student id/email
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    // Drop header row if it looks like one
    const dataLines = lines[0]?.toLowerCase().includes('email') ? lines.slice(1) : lines;
    const ids = dataLines.map((l) => l.split(',')[0].trim()).filter(Boolean);
    await enrollStudents(cohortId, ids);
    const mock: BulkEnrollResult = { enrolled: ids.length, skipped: 0, errors: [] };
    return mockFallback(error, mock);
  }
}

// =====================================================
// API: PROGRESS & STUDENTS DETAIL
// =====================================================

export async function getCohortProgress(id: string): Promise<CohortProgress> {
  try {
    const response = await sessionManagerApi.get(`/cohorts/${id}/progress`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch cohort progress:', error);
    const cohort = mockState.cohorts.find((c) => c.id === id);
    const labs = [
      { labId: 'lab-1', labTitle: 'Intro to OpenAI API' },
      { labId: 'lab-2', labTitle: 'Embeddings & Vector Search' },
      { labId: 'lab-3', labTitle: 'Prompt Engineering Patterns' },
      { labId: 'lab-4', labTitle: 'Building a RAG Pipeline' },
      { labId: 'lab-5', labTitle: 'Evaluation & Benchmarks' },
    ];
    const studentCount = cohort?.studentCount || 0;
    const mock: CohortProgress = {
      students: Array.from({ length: Math.min(studentCount, 10) }, (_, i) => {
        // Deterministic per-index numbers — no Math.random.
        const completed = Math.min(labs.length, (i * 7 + 3) % (labs.length + 1));
        return {
          studentId: cohort?.enrolledStudents[i] || `student-${i}`,
          name: `Student ${i + 1}`,
          completedLabs: completed,
          totalLabs: labs.length,
          lastActive: days((i + 1) % 7),
        };
      }),
      byLab: labs.map((l, i) => ({
        ...l,
        completionRate: Math.max(0, 95 - i * 18),
      })),
    };
    return mockFallback(error, mock);
  }
}

export async function getCohortStudents(id: string): Promise<CohortStudentRow[]> {
  try {
    const response = await sessionManagerApi.get(`/cohorts/${id}/students`);
    return response.data.students;
  } catch (error) {
    console.error('Failed to fetch cohort students:', error);
    const cohort = mockState.cohorts.find((c) => c.id === id);
    const firstNames = ['Ali', 'Sara', 'Omar', 'Hina', 'Bilal', 'Zara', 'Faisal', 'Mariam', 'Hamza', 'Ayesha'];
    const lastNames = ['Khan', 'Ahmed', 'Malik', 'Iqbal', 'Sheikh', 'Raza', 'Hussain', 'Siddiqui', 'Qureshi', 'Butt'];
    const statuses: CohortStudentRow['status'][] = ['ahead', 'on-track', 'on-track', 'behind', 'at-risk'];
    const rows: CohortStudentRow[] = (cohort?.enrolledStudents || []).map((sid, i) => {
      const first = firstNames[i % firstNames.length];
      const last = lastNames[(i * 3) % lastNames.length];
      return {
        studentId: sid,
        name: `${first} ${last}`,
        email: `${first.toLowerCase()}.${last.toLowerCase()}@example.pk`,
        enrolledAt: days(30 - (i % 30)),
        completedLabs: (i * 5 + 2) % 12,
        totalLabs: 12,
        lastActive: days(i % 14),
        status: statuses[i % statuses.length],
      };
    });
    return mockFallback(error, rows);
  }
}

// =====================================================
// HELPERS: course list for the cohort form
// =====================================================

export async function getCohortCourses(): Promise<Array<{ id: string; name: string }>> {
  try {
    const response = await sessionManagerApi.get('/cohorts/course-options');
    return response.data.courses || response.data;
  } catch (error) {
    return mockFallback(error, MOCK_COURSES);
  }
}
